import { useState, useCallback, useEffect } from 'react';
import * as api from '../api/vacations';

export function useVacationPolicies(autoLoad = true) {
  const [policies, setPolicies]   = useState([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.getVacationPolicies();
      setPolicies(res.data || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const save = useCallback(async (data) => {
    const res = await api.saveVacationPolicy(data);
    await load();
    return res;
  }, [load]);

  const remove = useCallback(async (id) => {
    await api.deleteVacationPolicy(id);
    await load();
  }, [load]);

  const initDefaults = useCallback(async () => {
    const res = await api.initDefaultPolicies();
    await load();
    return res;
  }, [load]);

  useEffect(() => { if (autoLoad) load(); }, [autoLoad, load]);

  return { policies, loading, error, load, save, remove, initDefaults };
}

export function useVacationBalances(year) {
  const [balances, setBalances]   = useState([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);

  const load = useCallback(async () => {
    if (!year) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.getAllBalances(year);
      setBalances(res.data || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [year]);

  const allocate = useCallback(async (employeeId = null, recalculate = false) => {
    const res = await api.allocateVacations({ year, employeeId, recalculate });
    await load();
    return res;
  }, [year, load]);

  const adjust = useCallback(async (employeeId, days, notes, field = 'allocated') => {
    const res = await api.adjustVacationBalance({ employeeId, year, days, notes, field });
    await load();
    return res;
  }, [year, load]);

  useEffect(() => { load(); }, [load]);

  return { balances, loading, error, load, allocate, adjust };
}

export function useVacationHistory(employeeId = null, year = null) {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.getVacationHistory({ employeeId, year });
      setTransactions(res.data || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [employeeId, year]);

  useEffect(() => { load(); }, [load]);

  return { transactions, loading, error, load };
}
